import * as SheetPrimitive from "@radix-ui/react-dialog";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { Button } from "../components/Button";
import { Icon } from "../components/Icon";
import { ScrollArea } from "../components/ScrollArea";
import { SheetViewportProvider } from "../components/SheetViewportContext";
import { XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;
const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (React.createElement(SheetPrimitive.Overlay, { className: cn("s-fixed s-inset-0 s-z-50", "s-bg-muted-foreground/75 dark:s-bg-muted-background-night/75", "data-[state=open]:s-animate-in data-[state=closed]:s-animate-out", "data-[state=closed]:s-fade-out-0 data-[state=open]:s-fade-in-0", className), ...props, ref: ref })));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
const SHEET_SIZES = ["md", "lg", "xl", "2xl", "3xl"];
const SHEET_SIDES = ["top", "bottom", "left", "right"];
const sizeClasses = {
    md: "sm:s-max-w-md",
    lg: "sm:s-max-w-xl",
    xl: "sm:s-max-w-3xl",
    "2xl": "sm:s-max-w-4xl",
    "3xl": "sm:s-max-w-5xl",
};
const sheetVariants = cva(cn("s-fixed s-z-50 s-overflow-hidden s-flex s-flex-col s-h-full s-w-full", "s-bg-background dark:s-bg-background-night", "s-transition s-ease-in-out data-[state=open]:s-animate-in data-[state=closed]:s-animate-out data-[state=closed]:s-duration-300 data-[state=open]:s-duration-500"), {
    variants: {
        side: {
            top: cn("s-inset-x-0 s-top-0 data-[state=closed]:s-slide-out-to-top data-[state=open]:s-slide-in-from-top", "s-border-b dark:s-border-border-night s-border-border"),
            bottom: cn("s-inset-x-0 s-bottom-0 data-[state=closed]:s-slide-out-to-bottom data-[state=open]:s-slide-in-from-bottom", "s-border-t dark:s-border-border-night s-border-border"),
            left: cn("s-inset-y-0 s-left-0 data-[state=closed]:s-slide-out-to-left data-[state=open]:s-slide-in-from-left", "s-border-r dark:s-border-border-night s-border-border"),
            right: cn("s-inset-y-0 s-right-0 data-[state=closed]:s-slide-out-to-right data-[state=open]:s-slide-in-from-right", "s-border-l dark:s-border-border-night s-border-border"),
        },
        size: sizeClasses,
    },
    defaultVariants: {
        side: "right",
        size: "md",
    },
});
const SheetContent = React.forwardRef(({ className, children, size, side, trapFocusScope, preventAutoFocusOnClose = true, preventAutoFocusOnOpen = true, onCloseAutoFocus, onOpenAutoFocus, ...props }, ref) => {
    const handleCloseAutoFocus = React.useCallback((event) => {
        if (preventAutoFocusOnClose) {
            event.preventDefault();
        }
        onCloseAutoFocus === null || onCloseAutoFocus === void 0 ? void 0 : onCloseAutoFocus(event);
    }, [preventAutoFocusOnClose, onCloseAutoFocus]);
    const handleOpenAutoFocus = React.useCallback((event) => {
        if (preventAutoFocusOnOpen) {
            event.preventDefault();
        }
        onOpenAutoFocus === null || onOpenAutoFocus === void 0 ? void 0 : onOpenAutoFocus(event);
    }, [preventAutoFocusOnOpen, onOpenAutoFocus]);
    const onKeyDownCapture = React.useCallback((e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            const root = e.currentTarget;
            const target = root.querySelector('[data-sheet-save="true"]:not(:disabled)');
            if (target) {
                e.preventDefault();
                target.click();
            }
        }
    }, []);
    return (React.createElement(SheetPortal, null,
        React.createElement(SheetOverlay, null),
        React.createElement(FocusScope, { trapped: trapFocusScope, asChild: true },
            React.createElement(SheetPrimitive.Content, { ref: ref, className: cn(sheetVariants({ size, side }), className, "s-sheet s-text-foreground dark:s-text-foreground-night"), onCloseAutoFocus: handleCloseAutoFocus, onOpenAutoFocus: handleOpenAutoFocus, onKeyDownCapture: onKeyDownCapture, ...props }, children))));
});
SheetContent.displayName = SheetPrimitive.Content.displayName;
const SheetHeader = ({ className, children, hideButton = false, ...props }) => (React.createElement("div", { className: cn("s-z-50 s-flex s-flex-none s-flex-col s-p-5 s-text-left", "s-bg-background dark:s-bg-background-night", className), ...props },
    children,
    React.createElement(SheetClose, { asChild: true, className: "s-absolute s-right-3 s-top-4" }, !hideButton && React.createElement(Button, { icon: XClose, variant: "ghost", size: "sm" }))));
SheetHeader.displayName = "SheetHeader";
const ScrollContainer = ({ noScroll, className, children, }) => {
    const viewportRef = React.useRef(null);
    const [viewport, setViewport] = React.useState(null);
    React.useEffect(() => {
        setViewport(viewportRef.current);
    }, []);
    const value = React.useMemo(() => viewport, [viewport]);
    if (noScroll) {
        return React.createElement("div", { className: cn(className, "s-flex s-flex-col") }, children);
    }
    return (React.createElement(ScrollArea, { className: className, viewportRef: viewportRef },
        React.createElement(SheetViewportProvider, { value: value }, children)));
};
const SheetContainer = ({ children, noScroll, isListSelector, className, }) => {
    return (React.createElement(ScrollContainer, { noScroll: noScroll, className: cn("s-min-h-0 s-w-full s-flex-1 s-overflow-hidden", "s-border-t s-border-border/60 s-transition-all s-duration-300 dark:s-border-border-night/60") },
        React.createElement("div", { className: cn("s-relative s-flex s-min-h-0 s-flex-1 s-flex-col s-overflow-hidden s-text-left s-text-sm s-text-foreground dark:s-text-foreground-night", !isListSelector && "s-gap-5 s-px-5 s-pt-3", className) }, children)));
};
SheetContainer.displayName = "SheetContainer";
const SheetFooter = ({ className, children, leftButtonProps, rightButtonProps, rightEndButtonProps, sheetCloseClassName, ...props }) => {
    return (React.createElement("div", { className: cn("s-flex s-flex-none s-flex-col s-gap-2", "s-border-border dark:s-border-border-night", className), ...props },
        children,
        React.createElement("div", { className: "s-flex s-flex-row s-gap-2 s-border-t s-border-border s-p-3 dark:s-border-border-night" },
            leftButtonProps &&
                (leftButtonProps.disabled ? (React.createElement(Button, { ...leftButtonProps })) : (React.createElement(SheetClose, { className: sheetCloseClassName, asChild: true },
                    React.createElement(Button, { ...leftButtonProps })))),
            React.createElement("div", { className: "s-flex-grow" }),
            rightButtonProps &&
                (rightButtonProps.disabled ? (React.createElement(Button, { "data-sheet-save": "true", ...rightButtonProps })) : (React.createElement(SheetClose, { className: sheetCloseClassName, asChild: true },
                    React.createElement(Button, { "data-sheet-save": "true", ...rightButtonProps })))),
            rightEndButtonProps &&
                (rightEndButtonProps.disabled ? (React.createElement(Button, { ...rightEndButtonProps })) : (React.createElement(SheetClose, { className: sheetCloseClassName, asChild: true },
                    React.createElement(Button, { ...rightEndButtonProps })))))));
};
SheetFooter.displayName = "SheetFooter";
const SheetTitle = React.forwardRef(({ className, icon, ...props }, ref) => (React.createElement(React.Fragment, null,
    icon && (React.createElement(Icon, { visual: icon, size: "lg", className: "s-text-foreground dark:s-text-foreground-night" })),
    React.createElement(SheetPrimitive.Title, { ref: ref, className: cn("s-heading-lg", className), ...props }))));
SheetTitle.displayName = SheetPrimitive.Title.displayName;
const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (React.createElement(SheetPrimitive.Description, { ref: ref, className: cn("s-text-sm", "s-text-muted-foreground dark:s-text-muted-foreground-night", className), ...props })));
SheetDescription.displayName = SheetPrimitive.Description.displayName;
export { SheetViewportProvider, useSheetViewport, } from "../components/SheetViewportContext";
export { Sheet, SheetClose, SheetContainer, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger, };
//# sourceMappingURL=Sheet.js.map